/* eslint-disable */
import axios from "axios";
import store from "store";
import router from "@/router";
import tokenStorage from "@/services/tokenStorage";
import { ACCESS_SUSPENSION_TYPE, setAccessSuspensionForDb } from "@/services/accessSuspension";

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL + "/api/web",
  "Content-Type": "application/json",
  withCredentials: true, // Enable cookies (HTTP-only refresh token)
});

// Flag to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let failedQueue = [];
// Flag to track initial app refresh (on page load)
let isInitialRefresh = false;
let initialRefreshPromise = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Set initial refresh flag (called from App.vue before TryAutoLogin)
export const setInitialRefresh = (promise) => {
  isInitialRefresh = true;
  initialRefreshPromise = promise;
  promise
    .then(() => {
      isInitialRefresh = false;
      initialRefreshPromise = null;
      // Process any queued requests
      processQueue(null, tokenStorage.get());
    })
    .catch((error) => {
      isInitialRefresh = false;
      initialRefreshPromise = null;
      // Reject queued requests
      processQueue(error, null);
    });
};

apiClient.interceptors.request.use(async (request) => {
  // If initial refresh is in progress and this is not the refresh request itself,
  // wait for it to complete before proceeding
  if (isInitialRefresh && initialRefreshPromise && !request.url.includes("/admin-auth/refresh")) {
    try {
      await initialRefreshPromise;
    } catch (error) {
      // Initial refresh failed, but let the request proceed (it will get 401 and be handled)
      // The logout will be handled by the refresh failure handler
    }
  }

  const accessToken = tokenStorage.get(); // Get from in-memory storage
  const DB = store.get("DB");
  const url = request.url;

  // Set database header
  if (
    url.startsWith("/users") ||
    url.startsWith("/buildings") ||
    url.startsWith("/roles") ||
    url.startsWith("/admin-auth")
  ) {
    request.headers.db = "Root";
  } else {
    if (DB) {
      request.headers.db = DB;
    }
  }

  // Set authorization header
  if (accessToken) {
    request.headers.authorization = `Bearer ${accessToken}`;
  }

  // Set school year header
  const SchoolarYear = store.get("app.settings.activeSchoolarYear");
  if (SchoolarYear) request.headers.SchoolarYear = SchoolarYear;

  return request;
});

const TRIAL_BLOCK_CODE = "TRIAL_EXPIRED_NO_VALIDATED_OFFER";
const OVERDUE_BLOCK_CODE = "OVERDUE_PAYMENT_SUSPENDED";

// Response interceptor for automatic token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 403 &&
      error.response?.data?.errorCode === TRIAL_BLOCK_CODE
    ) {
      const currentDb = store.get("DB");
      setAccessSuspensionForDb(
        ACCESS_SUSPENSION_TYPE.TRIAL,
        currentDb,
        error.response?.data?.suspension
      );
    }

    if (
      error.response?.status === 403 &&
      error.response?.data?.errorCode === OVERDUE_BLOCK_CODE
    ) {
      const currentDb = store.get("DB");
      setAccessSuspensionForDb(
        ACCESS_SUSPENSION_TYPE.OVERDUE,
        currentDb,
        error.response?.data?.suspension
      );
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/web/admin-auth/refresh`,
          {},
          {
            withCredentials: true,
            headers: {
              db: "Root",
            },
          }
        );

        const { accessToken } = response.data;
        if (accessToken) {
          tokenStorage.set(accessToken); // Store in memory

          // Update the original request with new token
          originalRequest.headers.authorization = `Bearer ${accessToken}`;

          // Process queued requests
          processQueue(null, accessToken);

          // Retry the original request
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - logout and redirect to login
        processQueue(refreshError, null);
        tokenStorage.clear(); // Clear in-memory token

        // Use dynamic import to avoid circular dependency
        // Dispatch logout to clear all state and redirect to login
        import("@/store").then((storeModule) => {
          const vuexStore = storeModule.default;
          vuexStore.dispatch("user/LOGOUT");
        });

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
